import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TeamRole } from '@/consts/team'
import { cn } from '@/utils/style'

type Props = {
  value: TeamRole
  onChange: (next: TeamRole) => void
  options?: [TeamRole.Admin, TeamRole.Normal]
  className?: string
  disabled?: boolean
}

export function InlineDropDown({
  value,
  onChange,
  options = [TeamRole.Admin, TeamRole.Normal],
  className,
  disabled,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)

  // Helper function to get translated role label
  const getRoleLabel = (role: TeamRole): string => {
    if (role === TeamRole.Owner) return t('team.role.owner')
    if (role === TeamRole.Admin) return t('team.role.admin')
    return t('team.role.member')
  }
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return
      const t = e.target as Node
      if (btnRef.current?.contains(t)) return
      if (popRef.current?.contains(t)) return
      setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'justify-between h-8 w-36 px-4 rounded-lg select-none',
          'bg-neutral-50 text-primary font-semibold',
          'inline-flex items-center gap-2',
          'outline-none',
          'focus:ring-2 focus:ring-highlight focus:ring-offset-2 focus:ring-offset-panel-white',
          'focus-visible:ring-2 focus-visible:ring-highlight focus-visible:ring-offset-2 focus-visible:ring-offset-panel-white',
          open && 'ring-2 ring-highlight ring-offset-2 ring-offset-panel-white',
          'disabled:opacity-50',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{getRoleLabel(value)}</span>
        <Chevron
          className={cn('w-4 h-4 text-secondary transition-transform', open && 'rotate-180')}
        />
      </button>

      {open && (
        <div
          ref={popRef}
          role="listbox"
          className={cn(
            'absolute left-0 top-[35px] z-20',
            'min-w-[120px] rounded-xl bg-panel-white shadow-lg border border-neutral-200 py-1',
          )}
        >
          {options.map((opt) => {
            const active = opt === value
            return (
              <button
                key={opt}
                role="option"
                aria-selected={active}
                type="button"
                onClick={() => {
                  onChange(opt)
                  setOpen(false)
                }}
                className={cn(
                  'w-full text-left px-4 py-2.5',
                  't-sb',
                  active ? 'bg-highlight/10 text-primary' : 'text-primary',
                  'hover:bg-highlight/10 hover:rounded-lg',
                )}
              >
                {getRoleLabel(opt)}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Chevron(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M7 10l5 5 5-5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
